/**
 * <category-showcase> — sections/category-showcase.liquid
 * Each category image is stacked over the previous one and revealed from the
 * bottom while the image drifts into place, on hover/click or scrubbed by scroll.
 */
(function () {
  if (customElements.get("category-showcase")) return;

  const DESKTOP_QUERY = "(min-width: 990px)";
  const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
  const PARALLAX = 12;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  class CategoryShowcase extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll("[data-cts-slide]"));
      this.media = this.slides.map((slide) => slide.querySelector(".cts_slide_media"));
      this.triggers = Array.from(this.querySelectorAll("[data-cts-trigger]"));
      this.panels = Array.from(this.querySelectorAll("[data-cts-panel]"));

      if (!this.slides.length || !this.triggers.length) return;

      this.scrollStage = this.dataset.scroll === "true" ? this.closest(".cts_scroll_stage") : null;
      this.desktopQuery = window.matchMedia(DESKTOP_QUERY);
      this.reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
      this.activateOnClick = this.dataset.activate === "click";
      this.activeIndex = 0;
      this.scrollActive = null;

      this.onScroll = this.onScroll.bind(this);
      this.syncMode = this.syncMode.bind(this);

      this.triggers.forEach((trigger) => {
        const index = Number(trigger.dataset.index);

        trigger.addEventListener("pointerenter", () => {
          if (this.scrollActive || this.activateOnClick) return;
          this.select(index);
        });

        trigger.addEventListener("click", (event) => {
          if (this.scrollActive) {
            if (index === this.activeIndex && trigger.tagName === "A") return;
            event.preventDefault();
            this.scrollToSlide(index);
            return;
          }

          if (!this.activateOnClick || index === this.activeIndex) return;
          event.preventDefault();
          this.select(index);
        });

        trigger.addEventListener("focus", () => {
          const keyboard = trigger.matches(":focus-visible");
          if (this.scrollActive) {
            if (keyboard) this.scrollToSlide(index);
            return;
          }
          if (this.activateOnClick && !keyboard) return;
          this.select(index);
        });
      });

      this.desktopQuery.addEventListener("change", this.syncMode);
      this.reducedMotionQuery.addEventListener("change", this.syncMode);
      this.syncMode();
    }

    disconnectedCallback() {
      this.stopScroll();
      if (this.desktopQuery) this.desktopQuery.removeEventListener("change", this.syncMode);
      if (this.reducedMotionQuery) this.reducedMotionQuery.removeEventListener("change", this.syncMode);
    }

    get wantsScroll() {
      return Boolean(this.scrollStage) && this.desktopQuery.matches && !this.reducedMotionQuery.matches;
    }

    syncMode() {
      const scrollActive = this.wantsScroll;
      if (scrollActive === this.scrollActive) return;

      this.scrollActive = scrollActive;
      this.classList.toggle("is-scroll-driven", scrollActive);

      if (scrollActive) {
        window.addEventListener("scroll", this.onScroll, { passive: true });
        window.addEventListener("resize", this.onScroll, { passive: true });
        this.renderFromScroll();
      } else {
        this.stopScroll();
        this.slides.forEach((slide, i) => {
          slide.style.clipPath = "";
          if (this.media[i]) this.media[i].style.transform = "";
        });
        this.applyStack(this.activeIndex);
      }
    }

    stopScroll() {
      window.removeEventListener("scroll", this.onScroll);
      window.removeEventListener("resize", this.onScroll);
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    onScroll() {
      if (this.rafId) return;

      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.renderFromScroll();
      });
    }

    /* Scroll progress is measured in screens: one screen of scrolling reveals one category */
    getProgress() {
      const rect = this.scrollStage.getBoundingClientRect();
      const viewport = window.innerHeight || 1;
      const scrollable = Math.max(rect.height - viewport, 0);

      return clamp(-rect.top, 0, scrollable) / viewport;
    }

    renderFromScroll() {
      if (!this.scrollActive) return;

      const last = this.slides.length - 1;
      const progress = clamp(this.getProgress(), 0, last);

      this.slides.forEach((slide, i) => {
        const incoming = i === 0 ? 1 : clamp(progress - (i - 1), 0, 1);
        const outgoing = i === last ? 0 : clamp(progress - i, 0, 1);
        const offset = (1 - incoming) * PARALLAX - outgoing * (PARALLAX / 2);

        slide.style.clipPath = `inset(${((1 - incoming) * 100).toFixed(3)}% 0 0 0)`;
        slide.classList.toggle("is-revealed", incoming >= 1);
        if (this.media[i]) this.media[i].style.transform = `translate3d(0, ${offset.toFixed(3)}%, 0)`;
      });

      this.setActive(Math.min(Math.round(progress), last));
    }

    scrollToSlide(index) {
      if (!this.scrollStage) return;

      const top = this.scrollStage.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: top + index * window.innerHeight,
        behavior: this.reducedMotionQuery.matches ? "auto" : "smooth",
      });
    }

    select(index) {
      if (index === this.activeIndex || !this.slides[index]) return;

      this.setActive(index);
      this.applyStack(index);
    }

    /* Every slide up to the active one is revealed, so moving back retracts the ones above */
    applyStack(index) {
      this.slides.forEach((slide, i) => {
        slide.classList.toggle("is-revealed", i <= index);
      });
    }

    setActive(index) {
      if (index === this.activeIndex && this.activeSet) return;

      const changed = index !== this.activeIndex;
      this.activeIndex = index;
      this.activeSet = true;

      this.slides.forEach((slide, i) => {
        slide.classList.toggle("is-active", i === index);
        if (i === index) {
          slide.removeAttribute("aria-hidden");
        } else {
          slide.setAttribute("aria-hidden", "true");
        }
      });

      this.triggers.forEach((trigger) => {
        const isActive = Number(trigger.dataset.index) === index;
        trigger.classList.toggle("is-active", isActive);
        if (isActive) {
          trigger.setAttribute("aria-current", "true");
        } else {
          trigger.removeAttribute("aria-current");
        }
      });

      this.panels.forEach((panel) => {
        const isActive = Number(panel.dataset.index) === index;
        panel.hidden = !isActive;
        panel.classList.toggle("is-active", isActive);

        if (isActive && changed && !this.reducedMotionQuery.matches) {
          panel.classList.remove("is-animating");
          void panel.offsetWidth;
          panel.classList.add("is-animating");
        }
      });
    }
  }

  customElements.define("category-showcase", CategoryShowcase);
})();
